import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronDown, ChevronUp, Plus, Settings, Users, CalendarPlus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { format, addDays, parseISO } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";
import GroupForm from "./GroupForm";
import GroupMembers from "./GroupMembers";

type TrainingGroup = Tables<"training_groups"> & {
  profiles?: { full_name: string | null } | null;
};
type Season = Tables<"seasons">;

interface ScheduleSlot {
  day_of_week: number;
  start_time: string;
  duration_minutes: number;
  court_id: string | null;
}

const LEVEL_LABELS: Record<string, string> = {
  beginner: "Nybörjare",
  intermediate: "Medel",
  advanced: "Avancerad",
  elite: "Elit",
};

const AGE_LABELS: Record<string, string> = {
  junior_u10: "U10",
  junior_u12: "U12",
  junior_u14: "U14",
  junior_u16: "U16",
  junior_u18: "U18",
  adult: "Vuxen",
  senior: "Senior",
};

const DAY_LABELS = ["Sön", "Mån", "Tis", "Ons", "Tor", "Fre", "Lör"];

export default function AdminGroups({
  initialSeasonId,
  onBack,
}: {
  initialSeasonId?: string | null;
  onBack?: () => void;
}) {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const [seasonId, setSeasonId] = useState<string>(initialSeasonId ?? "");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "form" | "members">("list");
  const [editingGroup, setEditingGroup] = useState<TrainingGroup | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<TrainingGroup | null>(null);
  const [generateTarget, setGenerateTarget] = useState<TrainingGroup | null>(null);

  const { data: seasons = [] } = useQuery({
    queryKey: ["admin-seasons", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seasons")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data as Season[];
    },
    enabled: !!organization?.id,
  });

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["admin-groups", seasonId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("training_groups")
        .select("*, profiles!training_groups_coach_id_fkey(full_name)")
        .eq("season_id", seasonId)
        .eq("organization_id", organization!.id)
        .order("name");
      if (error) throw error;
      return data as TrainingGroup[];
    },
    enabled: !!seasonId && !!organization?.id,
  });

  const { data: memberCounts = {} } = useQuery({
    queryKey: ["admin-group-member-counts", seasonId],
    queryFn: async () => {
      const groupIds = groups.map((g) => g.id);
      if (groupIds.length === 0) return {};
      const { data, error } = await supabase
        .from("group_members")
        .select("group_id")
        .in("group_id", groupIds)
        .eq("status", "active");
      if (error) throw error;
      const counts: Record<string, number> = {};
      data.forEach((m) => {
        counts[m.group_id] = (counts[m.group_id] || 0) + 1;
      });
      return counts;
    },
    enabled: groups.length > 0,
  });

  // Auto-select first season
  if (!seasonId && seasons.length > 0) {
    setSeasonId(seasons[0].id);
  }

  const selectedSeason = seasons.find((s) => s.id === seasonId);

  const generateMutation = useMutation({
    mutationFn: async (group: TrainingGroup) => {
      if (!selectedSeason) throw new Error(t("seasons.groups.noSeasonSelected"));
      const schedule = (group.schedule as unknown as ScheduleSlot[]) || [];
      if (schedule.length === 0) throw new Error(t("seasons.groups.noSchedule"));

      const sessions: Array<{
        group_id: string;
        organization_id: string;
        coach_id: string | null;
        court_id: string | null;
        session_date: string;
        start_time: string;
        duration_minutes: number;
        status: string;
      }> = [];

      let current = parseISO(selectedSeason.start_date);
      const end = parseISO(selectedSeason.end_date);

      while (current <= end) {
        const dow = current.getDay();
        for (const slot of schedule) {
          if (slot.day_of_week === dow) {
            sessions.push({
              group_id: group.id,
              organization_id: organization!.id,
              coach_id: group.coach_id,
              court_id: slot.court_id,
              session_date: format(current, "yyyy-MM-dd"),
              start_time: slot.start_time,
              duration_minutes: slot.duration_minutes,
              status: "scheduled",
            });
          }
        }
        current = addDays(current, 1);
      }

      if (sessions.length === 0) throw new Error(t("seasons.groups.noSessionsToGenerate"));

      const { data: inserted, error } = await supabase
        .from("training_sessions")
        .insert(sessions)
        .select("id");
      if (error) throw error;

      // Create attendance for active members
      const { data: members } = await supabase
        .from("group_members")
        .select("player_id")
        .eq("group_id", group.id)
        .eq("status", "active");

      if (members && members.length > 0 && inserted) {
        const attendance = inserted.flatMap((session) =>
          members.map((m) => ({
            session_id: session.id,
            player_id: m.player_id,
            rsvp_status: "attending",
          }))
        );
        // Insert in batches of 500
        for (let i = 0; i < attendance.length; i += 500) {
          const batch = attendance.slice(i, i + 500);
          const { error: attErr } = await supabase
            .from("session_attendance")
            .insert(batch);
          if (attErr) throw attErr;
        }
      }

      return sessions.length;
    },
    onSuccess: (count) => {
      toast.success(t("seasons.groups.sessionsGenerated", { count }));
      setGenerateTarget(null);
    },
    onError: (e: Error) => toast.error(e.message || t("seasons.groups.couldNotGenerate")),
  });

  // Count expected sessions for confirmation dialog
  const countExpectedSessions = (group: TrainingGroup): number => {
    if (!selectedSeason) return 0;
    const schedule = (group.schedule as unknown as ScheduleSlot[]) || [];
    let count = 0;
    let current = parseISO(selectedSeason.start_date);
    const end = parseISO(selectedSeason.end_date);
    while (current <= end) {
      const dow = current.getDay();
      for (const slot of schedule) {
        if (slot.day_of_week === dow) count++;
      }
      current = addDays(current, 1);
    }
    return count;
  };

  if (viewMode === "form") {
    return (
      <GroupForm
        group={editingGroup}
        seasonId={seasonId}
        onBack={() => { setViewMode("list"); setEditingGroup(null); }}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["admin-groups"] });
          setViewMode("list");
          setEditingGroup(null);
        }}
      />
    );
  }

  if (viewMode === "members" && selectedGroup) {
    return (
      <GroupMembers
        group={selectedGroup}
        onBack={() => { setViewMode("list"); setSelectedGroup(null); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {onBack && (
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1 mb-2">
          <ArrowLeft className="h-4 w-4" /> {t("seasons.backToSeasons")}
        </Button>
      )}

      <div className="flex items-center justify-between">
        <Select value={seasonId} onValueChange={setSeasonId}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={t("seasons.selectSeason")} />
          </SelectTrigger>
          <SelectContent>
            {seasons.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          disabled={!seasonId}
          onClick={() => { setEditingGroup(null); setViewMode("form"); }}
          className="gap-1"
        >
          <Plus className="h-4 w-4" /> {t("seasons.groups.newGroup")}
        </Button>
      </div>

      {!seasonId ? (
        <p className="text-muted-foreground text-sm">{t("seasons.groups.selectSeasonToSeeGroups")}</p>
      ) : isLoading ? (
        <p className="text-muted-foreground text-sm">{t("seasons.loading")}</p>
      ) : groups.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("seasons.groups.noGroupsInSeason")}</p>
      ) : (
        groups.map((group) => {
          const expanded = expandedId === group.id;
          const schedule = (group.schedule as unknown as ScheduleSlot[]) || [];
          return (
            <Card key={group.id}>
              <CardHeader
                className="cursor-pointer py-3"
                onClick={() => setExpandedId(expanded ? null : group.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: group.color || "#3b82f6" }}
                    />
                    <CardTitle className="text-base">{group.name}</CardTitle>
                    <Badge variant="secondary">{AGE_LABELS[group.age_category] ?? group.age_category}</Badge>
                    <Badge variant="outline">{LEVEL_LABELS[group.level] ?? group.level}</Badge>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>{memberCounts[group.id] || 0}/{group.max_players} spelare</span>
                    {group.profiles?.full_name && <span>{group.profiles.full_name}</span>}
                    {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
              </CardHeader>
              {expanded && (
                <CardContent className="pt-0 space-y-3">
                  {schedule.length > 0 && (
                    <div className="text-sm space-y-1">
                      <span className="text-muted-foreground font-medium">{t("seasons.groups.schedule")}:</span>
                      {schedule.map((s, i) => (
                        <div key={i} className="ml-2">
                          {DAY_LABELS[s.day_of_week]} {s.start_time} ({s.duration_minutes} min)
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setEditingGroup(group); setViewMode("form"); }}
                      className="gap-1"
                    >
                      <Settings className="h-3.5 w-3.5" /> {t("seasons.edit")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedGroup(group); setViewMode("members"); }}
                      className="gap-1"
                    >
                      <Users className="h-3.5 w-3.5" /> {t("seasons.groups.players")}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setGenerateTarget(group)}
                      className="gap-1"
                      disabled={schedule.length === 0}
                    >
                      <CalendarPlus className="h-3.5 w-3.5" /> {t("seasons.groups.generateSessions")}
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })
      )}

      <AlertDialog open={!!generateTarget} onOpenChange={(open) => !open && setGenerateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("seasons.groups.generateSessionsTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {generateTarget && (
                <>
                  Detta skapar <strong>{countExpectedSessions(generateTarget)}</strong> pass för gruppen{" "}
                  <strong>{generateTarget.name}</strong> under hela terminen ({selectedSeason?.start_date} — {selectedSeason?.end_date}).
                  Befintliga pass påverkas inte.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("leagues.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => generateTarget && generateMutation.mutate(generateTarget)}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? t("seasons.groups.generating") : t("seasons.groups.generate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
